/**
 * Appends a visually-hidden "opens in a new window" notice to `target="_blank"`
 * links rendered from rich-text / metafield HTML.
 *
 * Primary path is server-side via `annotate-new-window-links` (so static HTML
 * audits see the cue). This script is progressive enhancement for content that
 * appears after load (section re-renders, late metafield hydration).
 *
 * Idempotent and scoped to rich-text containers so it never double-annotates
 * links that already carry the notice or an author-provided `aria-label`.
 */

const CONTAINER_SELECTOR = '.text-block, rte-formatter, .rte, .metafield-rich_text_field, .product-accordion__content';
const LINK_SELECTOR = 'a[target="_blank"]';
const ANNOTATED_ATTR = 'data-new-window-annotated';

/** @returns {string} The localized "opens in a new window" notice, or '' when unavailable. */
const getNoticeText = () => {
  const source = document.getElementById('a11y-new-window-message');
  return source ? (source.textContent || '').trim() : '';
};

/**
 * @param {Element} link
 * @param {string} notice
 * @returns {boolean} Whether the link already communicates the new-window notice.
 */
const isAlreadyAnnotated = (link, notice) => {
  if (link.hasAttribute(ANNOTATED_ATTR)) return true;
  if (link.hasAttribute('aria-label')) return true;
  if (link.querySelector('.visually-hidden')) return true;

  const text = (link.textContent || '').toLowerCase();
  return text.includes(notice.toLowerCase());
};

/**
 * Adds the new-window notice to eligible links within a root node.
 * @param {ParentNode} [root]
 */
const annotateNewWindowLinks = (root = document) => {
  const notice = getNoticeText();
  if (!notice) return;

  const scopes =
    root instanceof Element && root.matches(CONTAINER_SELECTOR)
      ? [root]
      : [...root.querySelectorAll(CONTAINER_SELECTOR)];

  for (const scope of scopes) {
    for (const link of scope.querySelectorAll(LINK_SELECTOR)) {
      if (isAlreadyAnnotated(link, notice)) continue;

      const span = document.createElement('span');
      span.className = 'visually-hidden';
      span.textContent = `, ${notice}`;
      link.appendChild(span);
      link.setAttribute(ANNOTATED_ATTR, 'true');
    }
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => annotateNewWindowLinks());
} else {
  annotateNewWindowLinks();
}

// Re-run when the theme editor re-renders a section.
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  annotateNewWindowLinks(target instanceof Element ? target : document);
});
